/* Outbound campaign audience editor.
 *
 * Layout: rules list (field/op/value, ANDed) + manual includes + manual
 * excludes. Match count is computed client-side against /api/customers so
 * the operator sees the impact of each change immediately. Save persists
 * the config to /api/outbound-audience; the bot will consume it from there
 * once the audience-driven dialer lands. */

import { useEffect, useMemo, useState } from "react";

import { apiFetch, fmtPhone } from "../lib/api";

// ─── Types ─────────────────────────────────────────────────────────────
type FieldType = "number" | "enum";

type FieldDef = {
  label: string;
  type: FieldType;
  enumValues?: { value: string; label: string }[];
  numberStep?: number;
  numberMin?: number;
  numberMax?: number;
  hint?: string;
};

const FIELD_DEFS: Record<string, FieldDef> = {
  status: {
    label: "Status",
    type: "enum",
    enumValues: [
      { value: "active", label: "Active" },
      { value: "pending_cancel", label: "Pending cancel" },
      { value: "cancelled", label: "Cancelled" },
    ],
  },
  segment: {
    label: "Segment",
    type: "enum",
    enumValues: [
      { value: "smb", label: "SMB" },
      { value: "mid_market", label: "Mid-market" },
      { value: "enterprise", label: "Enterprise" },
    ],
  },
  churn_risk: {
    label: "Churn risk",
    type: "number",
    numberStep: 0.05,
    numberMin: 0,
    numberMax: 1,
    hint: "0.0–1.0",
  },
  tenure_months: {
    label: "Tenure (months)",
    type: "number",
    numberStep: 1,
    numberMin: 0,
  },
  monthly_charge_cents: {
    label: "Monthly charge (¢)",
    type: "number",
    numberStep: 100,
    numberMin: 0,
    hint: "in cents",
  },
  prior_save_attempts: {
    label: "Prior save attempts",
    type: "number",
    numberStep: 1,
    numberMin: 0,
  },
  clv_score: {
    label: "CLV score",
    type: "number",
    numberStep: 1,
  },
  lines: {
    label: "Lines on account",
    type: "number",
    numberStep: 1,
    numberMin: 0,
  },
};

const NUMBER_OPS = [
  { value: "eq",  label: "=" },
  { value: "neq", label: "≠" },
  { value: "gt",  label: ">" },
  { value: "gte", label: "≥" },
  { value: "lt",  label: "<" },
  { value: "lte", label: "≤" },
];

const ENUM_OPS = [
  { value: "eq",  label: "is" },
  { value: "neq", label: "is not" },
];

type AudienceRule = {
  field: string;
  op: string;
  value: string | number;
};

type AudienceConfig = {
  rules: AudienceRule[];
  includes: number[];
  excludes: number[];
  updated_at: string;
};

// Wider customer shape than CustomersPanel uses — we filter on every
// numeric/enum column the worker accepts. Nullable on every numeric field
// because soft-deleted rows pre-extension may not have backfilled values.
type AudienceCustomer = {
  id: number;
  phone: string;
  first_name: string | null;
  last_name: string | null;
  business_name: string | null;
  status: string;
  segment: string | null;
  churn_risk: number;
  tenure_months: number;
  monthly_charge_cents: number;
  prior_save_attempts: number | null;
  clv_score: number | null;
  lines: number | null;
};

// ─── Match evaluation (mirrors the worker; eval'd locally for live count) ─
function matchesRule(c: AudienceCustomer, rule: AudienceRule): boolean {
  const v = (c as unknown as Record<string, unknown>)[rule.field];
  if (v === null || v === undefined) return false;
  switch (rule.op) {
    case "eq":  return v === rule.value;
    case "neq": return v !== rule.value;
    case "gt":  return Number(v) >  Number(rule.value);
    case "gte": return Number(v) >= Number(rule.value);
    case "lt":  return Number(v) <  Number(rule.value);
    case "lte": return Number(v) <= Number(rule.value);
    default:    return false;
  }
}

function computeMatches(
  config: AudienceConfig,
  customers: AudienceCustomer[],
): Set<number> {
  const exclude = new Set(config.excludes);
  const include = new Set(config.includes);
  const matched = new Set<number>();
  for (const c of customers) {
    if (exclude.has(c.id)) continue;
    if (config.rules.length === 0 && !include.has(c.id)) continue;
    if (config.rules.every((r) => matchesRule(c, r))) {
      matched.add(c.id);
    }
  }
  for (const id of include) {
    if (!exclude.has(id)) matched.add(id);
  }
  return matched;
}

function customerLabel(c: AudienceCustomer): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ");
  return name || c.business_name || fmtPhone(c.phone);
}

// ─── Component ─────────────────────────────────────────────────────────
export function AudienceEditor() {
  const [config, setConfig] = useState<AudienceConfig | null>(null);
  const [customers, setCustomers] = useState<AudienceCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [a, c] = await Promise.all([
          apiFetch<AudienceConfig>("/api/outbound-audience"),
          apiFetch<{ customers: AudienceCustomer[] }>("/api/customers"),
        ]);
        if (cancelled) return;
        setConfig(a);
        setSnapshot(JSON.stringify({ rules: a.rules, includes: a.includes, excludes: a.excludes }));
        setCustomers(c.customers);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const matched = useMemo(() => {
    if (!config) return new Set<number>();
    return computeMatches(config, customers);
  }, [config, customers]);

  const dirty = useMemo(() => {
    if (!config) return false;
    return (
      JSON.stringify({
        rules: config.rules,
        includes: config.includes,
        excludes: config.excludes,
      }) !== snapshot
    );
  }, [config, snapshot]);

  async function save() {
    if (!config || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const saved = await apiFetch<AudienceConfig>("/api/outbound-audience", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          rules: config.rules,
          includes: config.includes,
          excludes: config.excludes,
        }),
      });
      setConfig(saved);
      setSnapshot(
        JSON.stringify({
          rules: saved.rules,
          includes: saved.includes,
          excludes: saved.excludes,
        }),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div className="audience-editor audience-loading">Loading audience…</div>;
  }
  if (!config) {
    return (
      <div className="audience-editor audience-error">
        <div className="dial-err">
          {err ?? "Could not load audience config."}
        </div>
      </div>
    );
  }

  // ── mutators ──
  function addRule() {
    if (!config) return;
    // Object.keys()[0] is string | undefined under noUncheckedIndexedAccess;
    // bail if the catalog is empty rather than coercing.
    const firstField = Object.keys(FIELD_DEFS)[0];
    if (!firstField) return;
    const def = FIELD_DEFS[firstField];
    if (!def) return;
    const value =
      def.type === "enum" ? def.enumValues?.[0]?.value ?? "" : (def.numberMin ?? 0);
    setConfig({
      ...config,
      rules: [...config.rules, { field: firstField, op: "eq", value }],
    });
  }

  function updateRule(i: number, patch: Partial<AudienceRule>) {
    if (!config) return;
    const next = config.rules.slice();
    const cur = next[i];
    if (!cur) return;
    const merged = { ...cur, ...patch };
    // When the field changes, reset op + value to safe defaults for the new
    // field so the prior op (e.g. ">") doesn't linger on an enum.
    if (patch.field && patch.field !== cur.field) {
      const def = FIELD_DEFS[patch.field];
      if (def) {
        merged.op = "eq";
        merged.value =
          def.type === "enum"
            ? def.enumValues?.[0]?.value ?? ""
            : (def.numberMin ?? 0);
      }
    }
    next[i] = merged;
    setConfig({ ...config, rules: next });
  }

  function removeRule(i: number) {
    if (!config) return;
    setConfig({
      ...config,
      rules: config.rules.filter((_, idx) => idx !== i),
    });
  }

  function addInclude(id: number) {
    if (!config) return;
    if (config.includes.includes(id)) return;
    setConfig({
      ...config,
      includes: [...config.includes, id],
      excludes: config.excludes.filter((x) => x !== id),
    });
  }
  function removeInclude(id: number) {
    if (!config) return;
    setConfig({ ...config, includes: config.includes.filter((x) => x !== id) });
  }
  function addExclude(id: number) {
    if (!config) return;
    if (config.excludes.includes(id)) return;
    setConfig({
      ...config,
      excludes: [...config.excludes, id],
      includes: config.includes.filter((x) => x !== id),
    });
  }
  function removeExclude(id: number) {
    if (!config) return;
    setConfig({ ...config, excludes: config.excludes.filter((x) => x !== id) });
  }

  return (
    <div id="audience-editor" className="audience-editor">
      {/* ── Rules ── */}
      <div className="audience-section">
        <div className="audience-section-head">
          <span className="audience-section-label">Rules</span>
          <span className="audience-section-hint">
            All rules are ANDed. An empty rules list matches nobody — manual
            includes are still dialed.
          </span>
        </div>
        <ul id="audience-rules" className="audience-rules">
          {config.rules.map((rule, i) => (
            <RuleRow
              key={i}
              rule={rule}
              onChange={(patch) => updateRule(i, patch)}
              onRemove={() => removeRule(i)}
              index={i}
            />
          ))}
        </ul>
        <button
          id="audience-add-rule-btn"
          type="button"
          className="audience-add-btn"
          onClick={addRule}
        >
          + Add rule
        </button>
      </div>

      {/* ── Includes ── */}
      <SubscribersList
        variant="includes"
        ids={config.includes}
        customers={customers}
        otherIds={config.excludes}
        onAdd={addInclude}
        onRemove={removeInclude}
      />

      {/* ── Excludes ── */}
      <SubscribersList
        variant="excludes"
        ids={config.excludes}
        customers={customers}
        otherIds={config.includes}
        onAdd={addExclude}
        onRemove={removeExclude}
      />

      {/* ── Footer (count + save) ── */}
      <div id="audience-foot" className="audience-foot">
        <div
          id="audience-count"
          className="audience-count"
          title={`Matched by rules + manual includes − manual excludes`}
        >
          <strong>{matched.size}</strong>{" "}
          {matched.size === 1 ? "subscriber" : "subscribers"} will be called
        </div>
        <div className="audience-foot-spacer" />
        {err && <div className="dial-err">{err}</div>}
        <button
          id="audience-save-btn"
          type="button"
          className="audience-save"
          disabled={busy || !dirty}
          onClick={save}
        >
          {busy ? "Saving…" : dirty ? "Save audience" : "Saved"}
        </button>
      </div>
    </div>
  );
}

// ─── Rule row ───────────────────────────────────────────────────────────
function RuleRow({
  rule,
  onChange,
  onRemove,
  index,
}: {
  rule: AudienceRule;
  onChange: (patch: Partial<AudienceRule>) => void;
  onRemove: () => void;
  index: number;
}) {
  const def = FIELD_DEFS[rule.field];
  const ops = def?.type === "enum" ? ENUM_OPS : NUMBER_OPS;
  return (
    <li id={`audience-rule-${index}`} className="audience-rule-row">
      <select
        id={`audience-rule-field-${index}`}
        className="audience-rule-field"
        value={rule.field}
        onChange={(e) => onChange({ field: e.target.value })}
      >
        {Object.entries(FIELD_DEFS).map(([key, d]) => (
          <option key={key} value={key}>
            {d.label}
          </option>
        ))}
      </select>
      <select
        id={`audience-rule-op-${index}`}
        className="audience-rule-op"
        value={rule.op}
        onChange={(e) => onChange({ op: e.target.value })}
      >
        {ops.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {def?.type === "enum" ? (
        <select
          id={`audience-rule-value-${index}`}
          className="audience-rule-value"
          value={String(rule.value)}
          onChange={(e) => onChange({ value: e.target.value })}
        >
          {def.enumValues?.map((ev) => (
            <option key={ev.value} value={ev.value}>
              {ev.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={`audience-rule-value-${index}`}
          className="audience-rule-value"
          type="number"
          inputMode="decimal"
          step={def?.numberStep ?? 1}
          min={def?.numberMin}
          max={def?.numberMax}
          value={rule.value as number}
          onChange={(e) => onChange({ value: Number(e.target.value) })}
        />
      )}
      <button
        id={`audience-rule-remove-${index}`}
        type="button"
        className="audience-rule-remove"
        onClick={onRemove}
        aria-label="Remove rule"
        title="Remove rule"
      >
        ×
      </button>
    </li>
  );
}

// ─── Subscribers list (used for both includes and excludes) ─────────────
function SubscribersList({
  variant,
  ids,
  customers,
  otherIds,
  onAdd,
  onRemove,
}: {
  variant: "includes" | "excludes";
  ids: number[];
  customers: AudienceCustomer[];
  otherIds: number[];
  onAdd: (id: number) => void;
  onRemove: (id: number) => void;
}) {
  const heading = variant === "includes" ? "Always include" : "Always exclude";
  const hint =
    variant === "includes"
      ? "Subscribers added here are dialed even if they don't match the rules."
      : "Subscribers added here are skipped even if they match the rules.";
  const idSet = useMemo(() => new Set(ids), [ids]);
  const otherSet = useMemo(() => new Set(otherIds), [otherIds]);
  return (
    <div className={`audience-section audience-section-${variant}`}>
      <div className="audience-section-head">
        <span className="audience-section-label">{heading}</span>
        <span className="audience-section-hint">{hint}</span>
      </div>
      <ul id={`audience-${variant}-list`} className="audience-chips">
        {ids.length === 0 && (
          <li className="audience-chips-empty muted">none</li>
        )}
        {ids.map((id) => {
          const c = customers.find((c) => c.id === id);
          return (
            <li
              key={id}
              id={`audience-${variant}-chip-${id}`}
              className="audience-chip"
            >
              <span className="audience-chip-label">
                {c ? customerLabel(c) : `Subscriber #${id}`}
                {c && (
                  <span className="audience-chip-meta">{fmtPhone(c.phone)}</span>
                )}
              </span>
              <button
                id={`audience-${variant}-remove-${id}`}
                type="button"
                className="audience-chip-remove"
                onClick={() => onRemove(id)}
                aria-label="Remove"
              >
                ×
              </button>
            </li>
          );
        })}
      </ul>
      <SubscriberPicker
        variant={variant}
        customers={customers}
        excludeIds={idSet}
        warnIds={otherSet}
        onPick={onAdd}
      />
    </div>
  );
}

// ─── Subscriber picker — search + dropdown ──────────────────────────────
function SubscriberPicker({
  variant,
  customers,
  excludeIds,
  warnIds,
  onPick,
}: {
  variant: "includes" | "excludes";
  customers: AudienceCustomer[];
  excludeIds: Set<number>;
  warnIds: Set<number>;
  onPick: (id: number) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const candidates = customers.filter((c) => !excludeIds.has(c.id));
    if (!q) return candidates.slice(0, 8);
    return candidates
      .filter((c) =>
        customerLabel(c).toLowerCase().includes(q) || c.phone.includes(q),
      )
      .slice(0, 12);
  }, [query, customers, excludeIds]);

  function pick(id: number) {
    onPick(id);
    setQuery("");
    setOpen(false);
  }

  return (
    <div className="audience-picker">
      <input
        id={`audience-${variant}-picker-input`}
        className="audience-picker-input"
        type="text"
        placeholder="+ Add subscriber — search by name or phone"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onBlur={() => {
          // Delay so a click on a result fires before close.
          setTimeout(() => setOpen(false), 150);
        }}
      />
      {open && matches.length > 0 && (
        <ul
          id={`audience-${variant}-picker-results`}
          className="audience-picker-results"
        >
          {matches.map((c) => (
            <li
              key={c.id}
              id={`audience-${variant}-picker-row-${c.id}`}
              className={`audience-picker-row ${warnIds.has(c.id) ? "is-warn" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(c.id);
              }}
            >
              <span className="audience-picker-row-label">
                {customerLabel(c)}
              </span>
              <span className="audience-picker-row-meta">
                {fmtPhone(c.phone)}
                {warnIds.has(c.id) && (
                  <span className="audience-picker-row-warn">
                    {" · "}
                    on {variant === "includes" ? "exclude" : "include"} list
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {open && matches.length === 0 && query.trim() && (
        <ul className="audience-picker-results">
          <li className="audience-picker-row audience-picker-row-empty">
            No matches.
          </li>
        </ul>
      )}
    </div>
  );
}
